import { RefObject, useContext, useEffect, useRef, useState } from 'react'
import { API_BASE_URL, SITE_URL } from '../../lib/constants'
import Style from '../Page/Details.module.css'
import axios from 'axios'
import { LiteLoader } from '../CustomElements/Loader/Loader'
import { MainContext } from '../../contexts/MainContext'
import Payment from '../../public/assets/img/Payment/payment.jpg'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Breadcrumb from '../../UIComponents/Breadcrumb'
import MetaTags from '../MetaTags/MetaTags'
import CustomSelectInputs from '../CustomElements/CustomSelectInputs/CustomSelectInputs'
import {
	FaCheck,
	FaExclamationCircle,
	FaExclamationTriangle,
	FaAddressCard,
	FaTruck,
	FaFileInvoice,
	FaToolbox,
	FaPlus,
	FaInfoCircle,
	FaSignature,
	FaPaperPlane
} from 'react-icons/fa'

interface pageProps {
	heading: string
	content: string
	meta_title?: string
	meta_description?: string
	meta_keywords?: string
}

interface ItemRow {
	sku: string
	quantity: string | number
	reason: string
	reason_other: string
}

type Errors = Record<string, string>

// Formats YYYY-MM-DD date into 'Month Day, Year' (e.g. "July 27, 2026") for input display
const formatDateDisplay = (isoDate: string) => {
	if (!isoDate) return ''
	const [yyyy, mm, dd] = isoDate.split('-').map(Number)
	if (!yyyy || !mm || !dd) return isoDate
	return new Date(yyyy, mm - 1, dd).toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric'
	})
}

const inputCls = (err?: boolean) =>
	`w-full border ${err ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#52a0f2] focus:ring-[#52a0f2]'} rounded-md px-3 py-2.5 text-base sm:text-sm text-gray-800 bg-white focus:outline-none focus:ring-1 transition-colors placeholder:text-gray-400`
const labelCls = 'block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5'
const sectionHeaderCls = 'text-sm sm:text-base font-semibold text-white bg-[#52a0f2] px-3.5 sm:px-4 py-2.5 rounded-t-md flex items-center gap-2'
const sectionBodyCls = 'border border-t-0 border-gray-200 rounded-b-md p-3.5 sm:p-5 bg-white mb-6 shadow-sm'
const errMsg = (msg: string) => (
	<p className='text-red-500 text-xs mt-1 font-medium flex items-center gap-1'>
		<FaExclamationCircle className='shrink-0' />
		<span>{msg}</span>
	</p>
)

const Detail = (props: any) => {
	const [pageDetail, setPageDetail] = useState<any>({})
	const router = useRouter()
	const { setIsLoading } = useContext(MainContext)

	// Contact Info
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [clinicOrg, setClinicOrg] = useState('')
	const [phone, setPhone] = useState('')
	const [email, setEmail] = useState('')

	// Shipping Info
	const [shippingAddress1, setShippingAddress1] = useState('')
	const [shippingAddress2, setShippingAddress2] = useState('')
	const [shippingCity, setShippingCity] = useState('')
	const [shippingState, setShippingState] = useState('')
	const [shippingProvince, setShippingProvince] = useState('')
	const [shippingZip, setShippingZip] = useState('')
	const [shippingCountry, setShippingCountry] = useState('United States')

	// Countries & States API
	const [countriesList, setCountriesList] = useState<any[]>([])
	const [statesByCountry, setStatesByCountry] = useState<any[]>([])
	const [selectedCountryId, setSelectedCountryId] = useState<string | number>('')
	const [countryLoading, setCountryLoading] = useState(false)
	const [stateLoading, setStateLoading] = useState(false)
	const [showCountrySelectMenu, setshowCountrySelectMenu] = useState(false)
	const [showStateSelectMenu, setshowStateSelectMenu] = useState(false)

	const getStatesByCountries = async (countryId: any) => {
		if (!countryId) {
			setStatesByCountry([])
			return
		}
		setStateLoading(true)
		try {
			const response = await axios.get(`${SITE_URL}api/states/${countryId}`)
			setStatesByCountry(response?.data || [])
		} catch (error) {
			console.error('Error fetching states:', error)
			setStatesByCountry([])
		} finally {
			setStateLoading(false)
		}
	}

	const getCountries = async () => {
		setCountryLoading(true)
		try {
			const response = await axios.get(`${SITE_URL}api/countries`)
			const list = response?.data || []
			setCountriesList(list)
			const us = list.find((c: any) => c.name?.toLowerCase() === 'united states' || c.code === 'US')
			if (us) {
				setSelectedCountryId(us.id)
				setShippingCountry(us.name)
				getStatesByCountries(us.id)
			} else if (list.length > 0) {
				setSelectedCountryId(list[0].id)
				setShippingCountry(list[0].name)
				getStatesByCountries(list[0].id)
			}
		} catch (error) {
			console.error('Error fetching countries:', error)
		} finally {
			setCountryLoading(false)
		}
	}

	useEffect(() => {
		getCountries()
	}, [])

	const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const val = e.target.value
		setSelectedCountryId(val)
		const found = countriesList.find((c: any) => String(c.id) === String(val))
		if (found) {
			setShippingCountry(found.name)
			getStatesByCountries(found.id)
		} else {
			setShippingCountry('')
			setStatesByCountry([])
		}
		setShippingState('')
		setShippingProvince('')
		setErrors((p) => ({ ...p, shippingCountry: '', shippingState: '', shippingProvince: '' }))
	}

	// Invoice Info
	const [invoiceNumber, setInvoiceNumber] = useState('')

	// Product Info (4 rows initially)
	const [items, setItems] = useState<ItemRow[]>(
		Array.from({ length: 4 }, () => ({ sku: '', quantity: '1', reason: '', reason_other: '' }))
	)

	// Customer Certification
	const [certifyName, setCertifyName] = useState('')
	const [certifySignature, setCertifySignature] = useState('')
	const [certifyDate, setCertifyDate] = useState('')
	const [certifyAuthorized, setCertifyAuthorized] = useState(false)

	// Submission & error states
	const [errors, setErrors] = useState<Errors>({})
	const [submitting, setSubmitting] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	const [apiError, setApiError] = useState('')

	useEffect(() => {
		const getPageDetail = async () => {
			if (props?.slug) {
				const result = await fetch(`${API_BASE_URL}${props?.slug}`)
				const res = await result?.json()
				setPageDetail(res)
			}
		}
		getPageDetail()
	}, [props])

	useEffect(() => {
		setIsLoading(false)
	}, [setIsLoading])

	// Initialize date to today's date in YYYY-MM-DD format
	useEffect(() => {
		const today = new Date()
		const yyyy = today.getFullYear()
		const mm = String(today.getMonth() + 1).padStart(2, '0')
		const dd = String(today.getDate()).padStart(2, '0')
		setCertifyDate(`${yyyy}-${mm}-${dd}`)
	}, [])

	const updateItem = (idx: number, field: keyof ItemRow, val: string) => {
		setItems((prev) =>
			prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it))
		)
		setErrors((p) => {
			const copy = { ...p }
			delete copy.items
			delete copy[`item_${idx}_sku`]
			delete copy[`item_${idx}_reason`]
			delete copy[`item_${idx}_reason_other`]
			return copy
		})
	}

	const updateQuantity = (idx: number, val: string) => {
		if (val !== '' && Number(val) < 1) return
		updateItem(idx, 'quantity', val)
	}

	const addRow = () => {
		setItems((prev) => [...prev, { sku: '', quantity: '1', reason: '', reason_other: '' }])
	}

	const removeRow = (idx: number) => {
		if (idx >= 4) {
			setItems((prev) => prev.filter((_, i) => i !== idx))
			setErrors((p) => {
				const copy = { ...p }
				Object.keys(copy).forEach((key) => {
					if (key.startsWith('item_')) delete copy[key]
				})
				return copy
			})
		}
	}

	const validate = (): Errors => {
		const e: Errors = {}
		if (!firstName.trim()) e.firstName = 'First Name is required'
		if (!lastName.trim()) e.lastName = 'Last Name is required'
		if (!email.trim()) e.email = 'Email Address is required'
		else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Email Address is invalid'

		if (!shippingAddress1.trim()) e.shippingAddress1 = 'Shipping Address Line 1 is required'
		if (!shippingCity.trim()) e.shippingCity = 'City is required'
		if (!shippingCountry || shippingCountry === 'Select country' || !shippingCountry.trim()) e.shippingCountry = 'Country is required'

		if (statesByCountry.length > 0) {
			if (!shippingState || shippingState === 'Select state' || !shippingState.trim()) e.shippingState = 'State is required'
		} else {
			if (!shippingState.trim()) e.shippingState = 'State / Province is required'
		}

		if (!shippingZip.trim()) e.shippingZip = 'ZIP / Postal Code is required'

		const filledItems = items.filter(
			(it) => it.sku.trim() || it.reason || it.reason_other.trim()
		)

		if (filledItems.length === 0) {
			e.items = 'Please enter at least one item SKU to return.'
		} else {
			items.forEach((item, idx) => {
				const isActive = item.sku.trim() || item.reason || item.reason_other.trim()
				if (isActive) {
					if (!item.sku.trim()) {
						e[`item_${idx}_sku`] = `SKU is required for Item ${idx + 1}`
					}
					if (!item.reason) {
						e[`item_${idx}_reason`] = `Reason is required for Item ${idx + 1}`
					}
					if (item.reason === 'other' && !item.reason_other.trim()) {
						e[`item_${idx}_reason_other`] = `Details are required for Item ${idx + 1}`
					}
				}
			})
		}

		if (!certifyName.trim()) e.certifyName = 'Name is required'
		if (!certifySignature.trim()) e.certifySignature = 'Signature is required'
		if (!certifyDate.trim()) e.certifyDate = 'Date is required'
		if (!certifyAuthorized) e.certifyAuthorized = 'You must check this box to certify and authorize the request.'

		return e
	}

	const resetForm = () => {
		setFirstName('')
		setLastName('')
		setClinicOrg('')
		setPhone('')
		setEmail('')
		setShippingAddress1('')
		setShippingAddress2('')
		setShippingCity('')
		setShippingState('')
		setShippingProvince('')
		setShippingZip('')
		const us = countriesList.find((c: any) => c.name?.toLowerCase() === 'united states' || c.code === 'US')
		if (us) {
			setSelectedCountryId(us.id)
			setShippingCountry(us.name)
			getStatesByCountries(us.id)
		} else {
			setSelectedCountryId('')
			setShippingCountry('')
			setStatesByCountry([])
		}
		setInvoiceNumber('')
		setItems(Array.from({ length: 4 }, () => ({ sku: '', quantity: '1', reason: '', reason_other: '' })))
		setCertifyName('')
		setCertifySignature('')
		setCertifyAuthorized(false)
		const today = new Date()
		const yyyy = today.getFullYear()
		const mm = String(today.getMonth() + 1).padStart(2, '0')
		const dd = String(today.getDate()).padStart(2, '0')
		setshowCountrySelectMenu(false)
		setshowStateSelectMenu(false)
		setErrors({})
		setApiError('')
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		const errs = validate()
		if (Object.keys(errs).length > 0) {
			setErrors(errs)
			const firstErrEl = document.querySelector('[data-error="true"]')
			firstErrEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
			return
		}

		setErrors({})
		setApiError('')
		setSubmitting(true)

		try {
			const payload = {
				first_name: firstName,
				last_name: lastName,
				email: email,
				clinic_organization: clinicOrg,
				phone: phone,
				shipping_address_1: shippingAddress1,
				shipping_address_2: shippingAddress2,
				city: shippingCity,
				state: shippingState,
				province: shippingProvince,
				zip_code: shippingZip,
				country: shippingCountry,
				invoice_number: invoiceNumber,
				items: items
					.filter((it) => it.sku.trim() !== '')
					.map((it) => {
						const itemObj: any = {
							sku: it.sku.trim(),
							quantity: Number(it.quantity) || 1,
							reason: it.reason
						}
						if (it.reason === 'other' && it.reason_other.trim()) {
							itemObj.reason_other = it.reason_other.trim()
						}
						return itemObj
					}),
				certify_name: certifyName,
				certify_signature: certifySignature,
				certify_date: certifyDate,
		        not_bioactive: true,
                not_radioactive:true,
				not_dangerous:true
			}

			const response = await axios.post(`${API_BASE_URL}shipping-return`, payload)

			if (response?.status === 200 || response?.data) {
				setSubmitted(true)
				resetForm()
				window.scrollTo({ top: 0, behavior: 'smooth' })
				setTimeout(() => setSubmitted(false), 8000)
			}
		} catch (err: any) {
			console.error('Submission Exception:', err)
			setApiError(err?.response?.data?.message || 'An error occurred while submitting your RMA request. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	const isReturnsPage =
		Array.isArray(props?.slug)
			? props?.slug[0] === 'returns-refunds-and-exchanges'
			: props?.slug === 'returns-refunds-and-exchanges'

	return (
		<>
			<MetaTags
				title={props?.page?.meta_title}
				description={props?.page?.meta_description}
				keywords={props?.page?.meta_keywords}
			/>
			<Breadcrumb data={props?.page} />
			<div className='relative D_pages_wraper'>
				<div className='checkout-page-container width mt-7 md:mt-14 min-h-[400px]'>
					<h1 className='text-3xl lg:text-5xl sm:text-3xl font-bold'>
						{props?.page != undefined && props?.page?.heading}
					</h1>
					<div className='returning-customer rounded-lg my-4 mb-5 md:mb-14 leading-normal'>
						<div className='grid grid-cols-1 w-full gap-4 all-instruments-links'>
							<div
								className={Style.detail}
								dangerouslySetInnerHTML={{
									__html: props?.page != undefined ? props?.page?.content : ''
								}}
							></div>
						</div>
						{router.asPath === '/payment-info' && (
							<div className='paymentImgfooter'>
								<Image src={Payment} alt='payment' width={282} height={44} />
							</div>
						)}
					</div>

					{isReturnsPage && (
						<section className='rma-form-container max-w-5xl mx-auto px-3 sm:px-4 md:px-0 pb-12'>
							{submitted && (
								<div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-fade-in'>
									<div className='w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0'>
										<FaCheck className='text-green-600 text-lg' />
									</div>
									<div>
										<h4 className='text-green-800 font-bold'>Success!</h4>
										<p className='text-green-700 text-sm'>
											Your RMA request has been submitted successfully. We will review your request and get back to you shortly.
										</p>
									</div>
								</div>
							)}

							{apiError && (
								<div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3'>
									<div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0'>
										<FaExclamationTriangle className='text-red-600 text-lg' />
									</div>
									<div>
										<h4 className='text-red-800 font-bold'>Submission Error</h4>
										<p className='text-red-700 text-sm'>{apiError}</p>
									</div>
								</div>
							)}

							<form onSubmit={handleSubmit} noValidate>
								{/* 1. Contact Information */}
								<div>
									<div className={sectionHeaderCls}>
										<FaAddressCard className="text-white" /> Contact Information
									</div>
									<div className={sectionBodyCls}>
										<div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
											<div data-error={!!errors.firstName}>
												<label className={labelCls}>
													First Name <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={firstName}
													onChange={(e) => {
														setFirstName(e.target.value)
														setErrors((p) => ({ ...p, firstName: '' }))
													}}
													className={inputCls(!!errors.firstName)}
													placeholder='Enter first name'
												/>
												{errors.firstName && errMsg(errors.firstName)}
											</div>
											<div data-error={!!errors.lastName}>
												<label className={labelCls}>
													Last Name <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={lastName}
													onChange={(e) => {
														setLastName(e.target.value)
														setErrors((p) => ({ ...p, lastName: '' }))
													}}
													className={inputCls(!!errors.lastName)}
													placeholder='Enter last name'
												/>
												{errors.lastName && errMsg(errors.lastName)}
											</div>
											<div className='sm:col-span-2'>
												<label className={labelCls}>Clinic / Hospital / Organization</label>
												<input
													type='text'
													value={clinicOrg}
													onChange={(e) => setClinicOrg(e.target.value)}
													className={inputCls()}
													placeholder='Enter clinic, hospital, or organization name'
												/>
											</div>
											<div>
												<label className={labelCls}>Phone Number</label>
												<input
													type='tel'
													value={phone}
													onChange={(e) => setPhone(e.target.value)}
													className={inputCls()}
													placeholder='Enter phone number'
												/>
											</div>
											<div data-error={!!errors.email}>
												<label className={labelCls}>
													Email Address <span className='text-red-500'>*</span>
												</label>
												<input
													type='email'
													value={email}
													onChange={(e) => {
														setEmail(e.target.value)
														setErrors((p) => ({ ...p, email: '' }))
													}}
													className={inputCls(!!errors.email)}
													placeholder='Enter email address'
												/>
												{errors.email && errMsg(errors.email)}
											</div>
										</div>
									</div>
								</div>

								{/* 2. Shipping Information */}
								<div>
									<div className={sectionHeaderCls}>
										<FaTruck className="text-white" /> Shipping Information
									</div>
									<div className={sectionBodyCls}>
										<div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
											<div className='sm:col-span-2' data-error={!!errors.shippingAddress1}>
												<label className={labelCls}>
													Shipping Address Line 1 <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={shippingAddress1}
													onChange={(e) => {
														setShippingAddress1(e.target.value)
														setErrors((p) => ({ ...p, shippingAddress1: '' }))
													}}
													className={inputCls(!!errors.shippingAddress1)}
													placeholder='Street address, P.O. box'
												/>
												{errors.shippingAddress1 && errMsg(errors.shippingAddress1)}
											</div>
											<div className='sm:col-span-2'>
												<label className={labelCls}>Shipping Address Line 2</label>
												<input
													type='text'
													value={shippingAddress2}
													onChange={(e) => setShippingAddress2(e.target.value)}
													className={inputCls()}
													placeholder='Apartment, suite, unit, building, floor, etc.'
												/>
											</div>
											<div className='sm:col-span-2' data-error={!!errors.shippingCountry}>
												<label className={labelCls}>
													Country <span className='text-red-500'>*</span>
												</label>
												<CustomSelectInputs
													showSearch={true}
													dataLoading={countryLoading}
													input_errors={!!errors.shippingCountry}
													className='mt-1'
													data={countriesList}
													defaultOption={shippingCountry || 'Select country'}
													name='country'
													placeholder='Search country'
													showSelectMenu={showCountrySelectMenu}
													setshowSelectMenu={setshowCountrySelectMenu}
													onClick={(e: any) => {
														const countryId = Number(e.target.value) || e.target.value
														const countryName = e.target.title || e.target.value
														setSelectedCountryId(countryId)
														setShippingCountry(countryName)
														setShippingState('')
														setErrors((p) => ({ ...p, shippingCountry: '', shippingState: '' }))
														getStatesByCountries(countryId)
													}}
												/>
												{errors.shippingCountry && errMsg(errors.shippingCountry)}
											</div>
											<div data-error={!!errors.shippingState}>
												<label className={labelCls}>
													State (U.S. or Puerto Rico) <span className='text-red-500'>*</span>
												</label>
												{statesByCountry && statesByCountry.length > 0 ? (
													<CustomSelectInputs
														showSearch={true}
														countryNotSelected={!shippingCountry || shippingCountry === 'Select country'}
														dataLoading={stateLoading}
														input_errors={!!errors.shippingState}
														className='mt-1'
														data={statesByCountry}
														defaultOption={shippingState || 'Select state'}
														name='state'
														placeholder='Search state'
														showSelectMenu={showStateSelectMenu}
														setshowSelectMenu={setshowStateSelectMenu}
														onClick={(e: any) => {
															const stateName = e.target.title || e.target.value
															setShippingState(stateName)
															setErrors((p) => ({ ...p, shippingState: '' }))
														}}
													/>
												) : (
													<input
														type='text'
														value={shippingState}
														onChange={(e) => {
															setShippingState(e.target.value)
															setErrors((p) => ({ ...p, shippingState: '' }))
														}}
														className={inputCls(!!errors.shippingState)}
														placeholder={stateLoading ? 'Loading states...' : 'Enter state / province'}
														disabled={stateLoading}
													/>
												)}
												{errors.shippingState && errMsg(errors.shippingState)}
											</div>
											<div data-error={!!errors.shippingCity}>
												<label className={labelCls}>
													City <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={shippingCity}
													onChange={(e) => {
														setShippingCity(e.target.value)
														setErrors((p) => ({ ...p, shippingCity: '' }))
													}}
													className={inputCls(!!errors.shippingCity)}
													placeholder='Enter city'
												/>
												{errors.shippingCity && errMsg(errors.shippingCity)}
											</div>
											<div data-error={!!errors.shippingProvince}>
												<label className={labelCls}>
													Province (Canada) {shippingCountry === 'Canada' && <span className='text-red-500'>*</span>}
												</label>
												<input
													type='text'
													value={shippingProvince}
													onChange={(e) => {
														setShippingProvince(e.target.value)
														setErrors((p) => ({ ...p, shippingProvince: '' }))
													}}
													className={inputCls(!!errors.shippingProvince)}
													placeholder='Enter Canadian province'
												/>
												{errors.shippingProvince && errMsg(errors.shippingProvince)}
											</div>
											<div data-error={!!errors.shippingZip}>
												<label className={labelCls}>
													ZIP / Postal Code <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={shippingZip}
													onChange={(e) => {
														setShippingZip(e.target.value)
														setErrors((p) => ({ ...p, shippingZip: '' }))
													}}
													className={inputCls(!!errors.shippingZip)}
													placeholder='Enter ZIP or postal code'
												/>
												{errors.shippingZip && errMsg(errors.shippingZip)}
											</div>
											
										</div>
									</div>
								</div>

								{/* 3. Invoice Information */}
								<div>
									<div className={sectionHeaderCls}>
										<FaFileInvoice className="text-white" /> Invoice Information
									</div>
									<div className={sectionBodyCls}>
										<div>
											<label className={labelCls}>Invoice Number</label>
											<input
												type='text'
												value={invoiceNumber}
												onChange={(e) => setInvoiceNumber(e.target.value)}
												className={inputCls()}
												placeholder='Enter invoice number'
											/>
										</div>
									</div>
								</div>

								{/* 4. Product Information */}
								<div data-error={!!errors.items}>
									<div className={sectionHeaderCls}>
										<FaToolbox className="text-white" /> Product Information
									</div>
									<div className={`${sectionBodyCls} !p-0 overflow-hidden`}>
										{/* Desktop & Tablet Table View (md screens and up) */}
										<div className="hidden md:block overflow-x-auto">
											<table className="w-full border-collapse">
												<thead>
													<tr className="bg-gray-50/80 border-b border-gray-200 text-left">
														<th className="p-3 text-xs font-bold text-gray-700 uppercase w-12 text-center">#</th>
														<th className="p-3 text-xs font-bold text-gray-700 uppercase w-1/4">SKU *</th>
														<th className="p-3 text-xs font-bold text-gray-700 uppercase w-20 text-center">QTY</th>
														<th className="p-3 text-xs font-bold text-gray-700 uppercase w-1/4">REASON FOR RETURN</th>
														<th className="p-3 text-xs font-bold text-gray-700 uppercase">OTHER DETAILS</th>
														<th className="p-3 text-xs font-bold text-gray-700 uppercase w-10 text-center"></th>
													</tr>
												</thead>
												<tbody className="divide-y divide-gray-100">
													{items.map((item, idx) => (
														<tr key={idx} className="hover:bg-gray-50/50 transition-colors">
															<td className="p-3 text-sm text-gray-500 font-medium text-center">{idx + 1}</td>
															<td className="p-3" data-error={!!errors[`item_${idx}_sku`]}>
																<input
																	type="text"
																	value={item.sku}
																	onChange={(e) => updateItem(idx, 'sku', e.target.value)}
																	className={inputCls(!!errors[`item_${idx}_sku`])}
																	placeholder="SKU"
																/>
																{errors[`item_${idx}_sku`] && errMsg(errors[`item_${idx}_sku`])}
															</td>
															<td className="p-3">
																<input
																	type="number"
																	min="1"
																	step="1"
																	value={item.quantity}
																	onKeyDown={(e) => {
																		if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault()
																	}}
																	onChange={(e) => updateQuantity(idx, e.target.value)}
																	className={`${inputCls()} text-center`}
																/>
															</td>
															<td className="p-3" data-error={!!errors[`item_${idx}_reason`]}>
																<select
																	value={item.reason}
																	onChange={(e) => updateItem(idx, 'reason', e.target.value)}
																	className={inputCls(!!errors[`item_${idx}_reason`])}
																>
																	<option value="">Select Reason...</option>
																	<option value="sharpening">Sharpening</option>
																	<option value="repair">Repair</option>
																	<option value="return_exchange">Return / Exchange</option>
																	<option value="other">Other</option>
																</select>
																{errors[`item_${idx}_reason`] && errMsg(errors[`item_${idx}_reason`])}
															</td>
															<td className="p-3" data-error={!!errors[`item_${idx}_reason_other`]}>
																<input
																	type="text"
																	value={item.reason_other}
																	onChange={(e) => updateItem(idx, 'reason_other', e.target.value)}
																	disabled={item.reason !== 'other'}
																	className={`${inputCls(!!errors[`item_${idx}_reason_other`])} disabled:opacity-50 disabled:bg-gray-50`}
																	placeholder={item.reason === 'other' ? 'Please specify reason' : 'Select "Other" to write details'}
																/>
																{errors[`item_${idx}_reason_other`] && errMsg(errors[`item_${idx}_reason_other`])}
															</td>
															<td className="p-3 text-center">
																{idx >= 4 && (
																	<button
																		type="button"
																		onClick={() => removeRow(idx)}
																		className={`${Style.removeBtn} p-2 rounded-full inline-flex items-center justify-center focus:outline-none`}
																		title="Remove Item"
																		aria-label="Remove Item"
																	>
																		<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 fill-current" viewBox="0 0 20 20" fill="currentColor">
																			<path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
																		</svg>
																	</button>
																)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>

										{/* Mobile Card List View (screens below md) */}
										<div className="block md:hidden divide-y divide-gray-200">
											{items?.map((item, idx) => (
												<div key={idx} className="p-4 bg-white space-y-3 relative">
													<div className="flex items-center justify-between pb-2 border-b border-gray-100">
														<span className="font-bold text-xs uppercase tracking-wider text-gray-700">Item #{idx + 1}</span>
														{idx >= 4 && (
															<button
																type="button"
																onClick={() => removeRow(idx)}
																className={`${Style.removeBtn} px-2.5 py-1 rounded-md inline-flex items-center gap-1 text-xs font-semibold focus:outline-none`}
																title="Remove Item"
																aria-label="Remove Item"
															>
																<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 fill-current" viewBox="0 0 20 20" fill="currentColor">
																	<path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
																</svg>
																Remove
															</button>
														)}
													</div>
													<div className="grid grid-cols-3 gap-3">
														<div className="col-span-2" data-error={!!errors[`item_${idx}_sku`]}>
															<label className={labelCls}>SKU <span className="text-red-500">*</span></label>
															<input
																type="text"
																value={item.sku}
																onChange={(e) => updateItem(idx, 'sku', e.target.value)}
																className={inputCls(!!errors[`item_${idx}_sku`])}
																placeholder="Enter SKU"
															/>
															{errors[`item_${idx}_sku`] && errMsg(errors[`item_${idx}_sku`])}
														</div>
														<div>
															<label className={labelCls}>QTY</label>
															<input
																type="number"
																min="1"
																step="1"
																value={item.quantity}
																onKeyDown={(e) => {
																	if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault()
																}}
																onChange={(e) => updateQuantity(idx, e.target.value)}
																className={`${inputCls()} text-center`}
															/>
														</div>
													</div>
													<div data-error={!!errors[`item_${idx}_reason`]}>
														<label className={labelCls}>Reason for Return</label>
														<select
															value={item.reason}
															onChange={(e) => updateItem(idx, 'reason', e.target.value)}
															className={inputCls(!!errors[`item_${idx}_reason`])}
														>
															<option value="">Select Reason...</option>
															<option value="sharpening">Sharpening</option>
															<option value="repair">Repair</option>
															<option value="return_exchange">Return / Exchange</option>
															<option value="other">Other</option>
														</select>
														{errors[`item_${idx}_reason`] && errMsg(errors[`item_${idx}_reason`])}
													</div>
													<div data-error={!!errors[`item_${idx}_reason_other`]}>
														<label className={labelCls}>Other Details</label>
														<input
															type="text"
															value={item.reason_other}
															onChange={(e) => updateItem(idx, 'reason_other', e.target.value)}
															disabled={item.reason !== 'other'}
															className={`${inputCls(!!errors[`item_${idx}_reason_other`])} disabled:opacity-50 disabled:bg-gray-50`}
															placeholder={item.reason === 'other' ? 'Please specify reason' : 'Select "Other" to write details'}
														/>
														{errors[`item_${idx}_reason_other`] && errMsg(errors[`item_${idx}_reason_other`])}
													</div>
												</div>
											))}
										</div>

										{/* Add Row Button & General Error */}
										<div className="p-3.5 sm:p-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
											<button
												type="button"
												onClick={addRow}
												className="w-full sm:w-auto bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 font-semibold px-4 py-2.5 sm:py-2 rounded-[1.125rem] text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm"
											>
												<FaPlus className="text-[10px]" /> Add More Items
											</button>
											{errors.items && (
												<p className="text-red-500 text-xs font-medium flex items-center gap-1">
													<FaExclamationCircle className="shrink-0" />
													<span>{errors.items}</span>
												</p>
											)}
										</div>
									</div>
								</div>

								{/* 5. Returns and Restocking Fee Information */}
								<div>
									<div className={sectionHeaderCls}>
										<FaInfoCircle className="text-white" /> Returns & Restocking Fee Information
									</div>
									<div className={sectionBodyCls}>
										<div className="border border-gray-200 rounded p-4 bg-gray-50/50 text-sm text-gray-600 leading-relaxed">
											Instruments may be returned only if they are in their original condition and accompanied by all
											original packaging. A 15% restocking fee will apply to all approved returns and will be deducted
											from the refund. Items received without original packaging or showing signs of use, damage, or
											alteration may not be eligible for return or refund. Shipping charges are non-refundable.
											Returns are accepted within 30 days of the original purchase date.
										</div>
									</div>
								</div>

								{/* 6. Customer Certification */}
								<div>
									<div className={sectionHeaderCls}>
										<FaSignature className="text-white" /> Customer Certification
									</div>
									<div className={sectionBodyCls}>
										<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
											<div data-error={!!errors.certifyName}>
												<label className={labelCls}>
													Name <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={certifyName}
													onChange={(e) => {
														setCertifyName(e.target.value)
														setErrors((p) => ({ ...p, certifyName: '' }))
													}}
													className={inputCls(!!errors.certifyName)}
													placeholder='Enter your full name'
												/>
												{errors.certifyName && errMsg(errors.certifyName)}
											</div>

											<div data-error={!!errors.certifySignature}>
												<label className={labelCls}>
													Signature (Type to Sign) <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={certifySignature}
													onChange={(e) => {
														setCertifySignature(e.target.value)
														setErrors((p) => ({ ...p, certifySignature: '' }))
													}}
													className={inputCls(!!errors.certifySignature)}
													placeholder='Type full name to sign electronically'
												/>
												{errors.certifySignature && errMsg(errors.certifySignature)}
											</div>

											<div className="sm:col-span-2 md:col-span-1" data-error={!!errors.certifyDate}>
												<label className={labelCls}>
													Date <span className='text-red-500'>*</span>
												</label>
												<input
													type='text'
													value={formatDateDisplay(certifyDate)}
													readOnly
													className={`${inputCls(!!errors.certifyDate)} bg-gray-50 cursor-not-allowed`}
												/>
												{errors.certifyDate && errMsg(errors.certifyDate)}
											</div>
										</div>

										{/* Cursive Signature Preview Box */}
										<div className="mb-6 p-4 border border-dashed border-gray-300 rounded-md bg-gray-50/50 flex flex-col items-center justify-center min-h-[90px] text-center max-w-full overflow-hidden">
											<span className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2 select-none">
												ELECTRONIC SIGNATURE PREVIEW
											</span>
											{certifySignature ? (
												<span
													className="text-2xl sm:text-3xl font-semibold text-[#1a365d] select-none break-all max-w-full px-2"
													style={{ fontFamily: "'Brush Script MT', 'Dancing Script', cursive, sans-serif" }}
												>
													{certifySignature}
												</span>
											) : (
												<span className="text-gray-400 italic text-sm select-none">
													Your electronic signature will appear here
												</span>
											)}
										</div>

										{/* Certification Checkbox */}
										<div data-error={!!errors.certifyAuthorized} className="mt-4">
											<label className="flex items-start gap-3 cursor-pointer select-none group">
												<input
													type="checkbox"
													checked={certifyAuthorized}
													onChange={(e) => {
														setCertifyAuthorized(e.target.checked)
														setErrors((p) => ({ ...p, certifyAuthorized: '' }))
													}}
													className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-[#52a0f2] shrink-0"
												/>
												<span className="text-xs sm:text-sm text-gray-700 leading-normal">
													I certify that the information provided in this form is accurate. I also confirm that all returned products have been properly cleaned and decontaminated in accordance with applicable safety standards.
												</span>
											</label>
											{errors.certifyAuthorized && errMsg(errors.certifyAuthorized)}
										</div>
									</div>
								</div>

								{/* Form Actions */}
								<div className='flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4 pt-2'>
									<button
										type='submit'
										disabled={submitting}
										className='w-full sm:w-auto bg-[#52a0f2] hover:bg-[#1575d0] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-8 py-3.5 rounded-md text-sm uppercase tracking-wider transition-all shadow hover:shadow-md flex items-center justify-center gap-2 min-h-[48px]'
									>
										{submitting ? (
											<>
												<LiteLoader loaderType='sml' className='mr-2' /> Submitting...
											</>
										) : (
											<>
												<FaPaperPlane /> SUBMIT RMA REQUEST
											</>
										)}
									</button>
								</div>
							</form>
						</section>
					)}
				</div>
			</div>
		</>
	)
}

export default Detail
